/**
 * @copyright Copyright 2024-2025 Epic Systems Corporation
 * @file subsection that contains app links
 * @module Epic.AppOrchard.DownloadApp.AppLinksSection
 */

import { Flex, VStack } from "@chakra-ui/react";
import { AOLink, DownloadBuildIcon, DownloadBuildLink } from "ao/components/Core";
import { DownloadApprovedStatus } from "ao/data";
import { useSiteInfoState } from "ao/state/siteInfo";
import { IUserSecurity } from "ao/types";
import { IShowroomAppDownloadModel } from "ao/types/showroom";
import { getBaseUrl, isLocalhost } from "ao/utils/helpers";
import { FunctionComponent, memo, useMemo } from "react";

interface IProps {
	userSecurity: IUserSecurity;
	appDetails: IShowroomAppDownloadModel;
	isShowroom?: boolean;
}

export const AppLinksSection: FunctionComponent<IProps> = memo((props) => {
	const { appDetails, userSecurity, isShowroom } = props;

	const appModel = appDetails?.appModel;

	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);
	const isShowroomPublic = isShowroom && !isLocalhost() && !siteInfo.isAdminSite;

	const baseUrl = useMemo(() => (isShowroomPublic ? siteInfo.vendorServicesUrl : getBaseUrl()), [
		isShowroomPublic,
		siteInfo.vendorServicesUrl,
	]);

	return (
		<VStack spacing={3} align="stretch" textAlign="center">
			{appDetails.acceptedParticipantTermsId > 0 && userSecurity.canPurchase && (
				<AOLink
					alignSelf="center"
					url={`${baseUrl}Gallery/${siteInfo.customerAgreementName}?termsId=${appDetails.acceptedParticipantTermsId}`}
					isExternal
				>
					View {siteInfo.customerAgreementDisplayName}
				</AOLink>
			)}
			{appDetails.appModel.implementationGuideUrl && (
				<AOLink alignSelf="center" url={appDetails.appModel.implementationGuideUrl} isExternal>
					View this app's implementation guide
				</AOLink>
			)}
			{appModel.hasTCDocuments && (
				<>
					{appDetails.vendorApproved === DownloadApprovedStatus.Approved ||
					appDetails.vendorApproved === DownloadApprovedStatus.NonProdOnly ? (
						<Flex justify="center">
							<DownloadBuildLink appId={appModel.id} baseUrlOverride={baseUrl} showIcon />
						</Flex>
					) : (
						<Flex justify="center">
							<DownloadBuildIcon boxSize="16px" mr="2px" mt="3px" />
							Build available
						</Flex>
					)}
				</>
			)}
		</VStack>
	);
});
